import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { useProjects } from '../../hooks/useProjects.jsx'
import api from '../../services/api'
import { Briefcase, CheckCircle2, Clock, DollarSign, ArrowRight, Loader2 } from 'lucide-react'
import { formatDate } from '../../utils/helpers'
import { useTranslation } from 'react-i18next'

/**
 * Consultant's "My Projects" page — replaces the old mock-data version.
 * Shows only projects where the logged-in consultant is assigned.
 * For each project, summarises milestone progress + deliverable status.
 * "View Project" navigates to the project detail page where they can submit deliverables.
 */
function ConsultantProjects() {
  const { t } = useTranslation('consultants')
  const { t: tc } = useTranslation('common')
  const { user } = useAuth()
  const navigate = useNavigate()
  const { projects, loading: projectsLoading } = useProjects()
  const [activeTab, setActiveTab] = useState('active')
  const [stats, setStats] = useState({})  // per-project deliverable + milestone counts

  // The backend already scopes /projects/ to only what this consultant may see — which
  // INCLUDES any project where they own a milestone (not just the project's primary
  // consultant_id). Re-filtering on consultant_id here wrongly dropped projects where the
  // consultant owns a milestone but isn't the primary owner (e.g. a workshop/offer-to-more
  // copy), so they showed "no projects". Trust the backend scoping.
  const myProjects = projects || []
  const activeProjects = myProjects.filter(p => ['In Progress', 'Planning', 'Pending Approval'].includes(p.status))
  const completedProjects = myProjects.filter(p => ['Closed', 'Archived'].includes(p.status))

  // For each project, fetch milestones + deliverables to compute action dots
  useEffect(() => {
    if (!myProjects.length) return
    let cancelled = false
    Promise.all(myProjects.map(async (p) => {
      try {
        const [ms, dl] = await Promise.all([
          api.get(`/projects/${p.id}/milestones/`).catch(() => ({ data: [] })),
          api.get(`/deliverables/project/${p.id}`).catch(() => ({ data: [] })),
        ])
        return [p.id, { milestones: ms.data || [], deliverables: dl.data || [] }]
      } catch {
        return [p.id, { milestones: [], deliverables: [] }]
      }
    })).then(rows => {
      if (cancelled) return
      const next = {}
      for (const [id, val] of rows) next[id] = val
      setStats(next)
    })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myProjects.map(p => p.id).join(',')])

  // Compute the action dot for a project card
  const getProjectDot = (projectId) => {
    const ps = stats[projectId]
    if (!ps) return null
    const { milestones, deliverables } = ps
    // Invoice needs confirmation
    if (milestones.some(m => m.invoice_triggered && !m.consultant_invoice_confirmed))
      return { color: 'bg-amber-400', label: 'Confirm invoice pending', pulse: true }
    // Revision requested
    if (deliverables.some(d => d.status === 'Revision Requested'))
      return { color: 'bg-red-500', label: 'Revision needed', pulse: true }
    // All confirmed, awaiting mgmt
    if (milestones.some(m => m.consultant_invoice_confirmed && !(m.admin_invoice_approved && m.pm_invoice_approved)))
      return { color: 'bg-blue-400', label: 'Awaiting management approval', pulse: false }
    return null
  }

  // Value + earnings must reflect only THIS consultant's own milestones — the project's
  // total_budget/spent_budget are project-wide (other consultants + seeded figures) and were
  // showing wrong/clashing numbers. Compute from the milestones we already fetched into stats.
  const myMilestone = (m) => m.consultant_id === user?.id || m.consultant_id == null
  // Prefer the recorded claimed amount whenever it's set (reimbursable/Actual milestones —
  // on a pure-Actual project the per-milestone payment_mode often stays 'Fixed', so keying
  // off payment_mode alone wrongly showed $0). Fall back to the agreed fixed amount.
  const msAmount = (m) => {
    const claimed = Number(m.claimed_amount || 0)
    return claimed > 0 ? claimed : Number(m.payment_amount || 0)
  }
  const myMilestones = Object.values(stats).flatMap(s => (s.milestones || []).filter(myMilestone))
  const totalValue = myMilestones.reduce((s, m) => s + msAmount(m), 0)
  const totalEarned = myMilestones.filter(m => m.status === 'Paid').reduce((s, m) => s + msAmount(m), 0)
  // Per-project value for THIS consultant (their own milestones on that project).
  const projectMyValue = (projectId) => (stats[projectId]?.milestones || [])
    .filter(myMilestone).reduce((s, m) => s + msAmount(m), 0)

  const getStatusColor = (status) => ({
    'In Progress': 'bg-blue-100 text-blue-800',
    'Pending Approval': 'bg-amber-100 text-amber-800',
    'Planning': 'bg-cyan-100 text-cyan-800',
    'Closed': 'bg-gray-200 text-gray-700',
    'Archived': 'bg-gray-100 text-gray-500',
  }[status] || 'bg-gray-100 text-gray-700')

  const tabs = [
    { id: 'active', name: 'Active Projects', count: activeProjects.length },
    { id: 'completed', name: 'Completed', count: completedProjects.length },
  ]

  if (projectsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-7 h-7 text-blue-600 animate-spin" />
      </div>
    )
  }

  const renderProjectCard = (project) => {
    const dot = getProjectDot(project.id)
    const ps = stats[project.id] || { milestones: [], deliverables: [] }
    const totalMs = ps.milestones.length
    const approvedMs = ps.milestones.filter(m => m.consultant_accepted || m.status === 'Approved' || m.status === 'Paid' || m.status === 'Completed').length
    const paidMs = ps.milestones.filter(m => m.status === 'Paid').length
    const submittedD = ps.deliverables.filter(d => d.status === 'Submitted').length
    const revisionD = ps.deliverables.filter(d => d.status === 'Revision Requested').length
    const approvedD = ps.deliverables.filter(d => d.status === 'Approved').length

    return (
      <div key={project.id} className={`bg-white border rounded-xl p-5 hover:shadow-md transition-shadow ${dot?.pulse ? 'border-amber-300' : 'border-gray-200'}`}>
        {/* Action dot banner at the top of the card */}
        {dot && (
          <div className={`flex items-center gap-1.5 text-xs font-semibold text-white px-3 py-1.5 rounded-lg mb-3 w-fit ${dot.color} ${dot.pulse ? 'animate-pulse' : ''}`}>
            <span className="w-1.5 h-1.5 rounded-full bg-white/70 shrink-0" />
            {dot.label} — Click View Project to continue
          </div>
        )}
        <div className="flex flex-col md:flex-row md:items-start justify-between mb-3 gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <h3 className="text-lg font-semibold text-gray-900">{project.name}</h3>
              {/* Our canonical Project ID — the reference quoted on contracts, invoices and
                  emails, so a consultant can match what they're looking at to their paperwork. */}
              {(project.project_number || project.projectNumber) && (
                <span
                  title={t('t_project_id')}
                  className="font-mono text-[10px] px-1.5 py-0.5 rounded border bg-gray-50 text-gray-600 border-gray-200"
                >
                  {project.project_number || project.projectNumber}
                </span>
              )}
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(project.status)}`}>
                {project.status}
              </span>
            </div>
            {project.description && (
              <p className="text-sm text-gray-600 line-clamp-2 mb-2">{project.description}</p>
            )}
          </div>
          <div className="text-left md:text-right shrink-0">
            <p className="text-xs text-gray-500">{t('my_milestones_value')}</p>
            <p className="text-lg font-bold text-gray-900">${Number(projectMyValue(project.id)).toLocaleString()}</p>
          </div>
        </div>

        {/* Per-project milestone + deliverable summary */}
        <div className="grid grid-cols-3 gap-3 my-4 pb-4 border-b border-gray-100">
          <div className="bg-blue-50 rounded-lg p-2.5 text-center">
            <p className="text-[10px] text-blue-700 uppercase tracking-wide font-semibold">{t('milestones')}</p>
            <p className="text-sm font-bold text-gray-900 mt-0.5">{approvedMs} / {totalMs}</p>
            <p className="text-[10px] text-gray-500">approved</p>
          </div>
          <div className="bg-green-50 rounded-lg p-2.5 text-center">
            <p className="text-[10px] text-green-700 uppercase tracking-wide font-semibold">{t('deliverables')}</p>
            <p className="text-sm font-bold text-gray-900 mt-0.5">{approvedD}</p>
            <p className="text-[10px] text-gray-500">approved</p>
          </div>
          <div className="bg-amber-50 rounded-lg p-2.5 text-center">
            <p className="text-[10px] text-amber-700 uppercase tracking-wide font-semibold">{t('awaiting_action')}</p>
            <p className="text-sm font-bold text-gray-900 mt-0.5">{submittedD + revisionD}</p>
            <p className="text-[10px] text-gray-500">{revisionD > 0 ? `${revisionD} revisions` : 'submitted'}</p>
          </div>
        </div>

        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="text-xs text-gray-500">
            PM: {project.project_manager_name || project.projectManager || 'Unassigned'}
            {project.end_date && <> · Due {formatDate(project.end_date)}</>}
          </div>
          <button
            onClick={() => navigate(`/projects/${project.id}`)}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            View Project <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="bg-linear-to-r from-indigo-600 to-purple-600 rounded-xl p-6 text-white">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">{t('my_consulting_projects')}</h1>
            <p className="mt-1 opacity-90">{t('submit_track_paid')}</p>
          </div>
          <div className="text-left md:text-right">
            <p className="text-sm opacity-90">{t('my_total_value')}</p>
            <p className="text-3xl font-bold">${totalValue.toLocaleString()}</p>
            {user?.specialization && (
              <p className="text-xs opacity-90 mt-1">Specialization: {user.specialization}</p>
            )}
          </div>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card p-5">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-100"><Briefcase className="w-5 h-5 text-blue-600" /></div>
            <div>
              <p className="text-xs text-gray-500">{tc('status.active')}</p>
              <p className="text-xl font-bold text-gray-900">{activeProjects.length}</p>
            </div>
          </div>
        </div>
        <div className="card p-5">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-green-100"><CheckCircle2 className="w-5 h-5 text-green-600" /></div>
            <div>
              <p className="text-xs text-gray-500">{tc('status.completed')}</p>
              <p className="text-xl font-bold text-gray-900">{completedProjects.length}</p>
            </div>
          </div>
        </div>
        <div className="card p-5">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-100"><Clock className="w-5 h-5 text-amber-600" /></div>
            <div>
              <p className="text-xs text-gray-500">{t('total_projects')}</p>
              <p className="text-xl font-bold text-gray-900">{myProjects.length}</p>
            </div>
          </div>
        </div>
        <div className="card p-5">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-emerald-100"><DollarSign className="w-5 h-5 text-emerald-600" /></div>
            <div>
              <p className="text-xs text-gray-500">{t('earnings_to_date')}</p>
              <p className="text-xl font-bold text-gray-900">${totalEarned.toLocaleString()}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="card">
        <div className="border-b border-gray-200 overflow-x-auto">
          <nav className="flex space-x-6 px-6">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-3.5 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${activeTab === tab.id ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
              >
                {tab.name}
                <span className={`px-2 py-0.5 rounded-full text-xs ${activeTab === tab.id ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-600'}`}>{tab.count}</span>
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6 space-y-4">
          {activeTab === 'active' && (
            activeProjects.length === 0
              ? <div className="text-center py-12 text-gray-500"><Briefcase className="w-10 h-10 mx-auto text-gray-300 mb-2" /><p className="text-sm">{t('no_active_assigned')}</p></div>
              : activeProjects.map(renderProjectCard)
          )}
          {activeTab === 'completed' && (
            completedProjects.length === 0
              ? <div className="text-center py-12 text-gray-500"><CheckCircle2 className="w-10 h-10 mx-auto text-gray-300 mb-2" /><p className="text-sm">{t('no_completed')}</p></div>
              : completedProjects.map(renderProjectCard)
          )}
        </div>
      </div>
    </div>
  )
}

export default ConsultantProjects
